import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock, Calendar, ArrowRight } from "lucide-react";
import { getPost, getOthers } from "@/data/posts";
import { Container, Section, Reveal, GlowOrb } from "@/components/kit";
import { CtaBand } from "@/components/CtaBand";

const Block = ({ block }) => {
  if (block.type === "h2")
    return (
      <h2 className="mt-12 mb-4 font-display text-2xl md:text-3xl font-medium tracking-tight text-white">
        {block.text}
      </h2>
    );
  if (block.type === "quote")
    return (
      <blockquote className="my-10 border-l-2 border-gold pl-6">
        <p className="font-display text-xl md:text-2xl font-medium leading-snug text-white">
          “{block.text}”
        </p>
      </blockquote>
    );
  if (block.type === "ul")
    return (
      <ul className="my-6 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-white/70 text-base md:text-lg leading-relaxed">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
            {it}
          </li>
        ))}
      </ul>
    );
  return <p className="my-5 text-white/70 text-base md:text-lg leading-relaxed">{block.text}</p>;
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    return (
      <div data-testid="blog-post-notfound" className="pt-44 pb-32">
        <Container className="text-center">
          <p className="font-mono-jb text-sm uppercase tracking-widest text-gold/80">404</p>
          <h1 className="mt-4 font-display text-3xl md:text-5xl font-medium text-white">
            That article moved
          </h1>
          <p className="mt-4 text-white/60">The playbook you're after isn't here anymore.</p>
          <Link
            to="/blog"
            className="mt-8 inline-flex items-center gap-2 font-semibold text-gold hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Insights
          </Link>
        </Container>
      </div>
    );
  }

  const related = getOthers(slug).slice(0, 3);

  return (
    <div data-testid="blog-post-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-10 md:pt-40">
        <GlowOrb className="left-1/2 top-0 h-[420px] w-[620px] -translate-x-1/2 opacity-70" />
        <Container className="max-w-3xl">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 font-mono-jb text-xs uppercase tracking-widest text-white/50 hover:text-gold transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Insights
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <span className="rounded-full border border-gold/25 bg-gold/5 px-3 py-1 font-mono-jb text-[11px] uppercase tracking-widest text-gold/80">
              {post.category}
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono-jb text-xs text-white/45">
              <Calendar className="h-3.5 w-3.5" /> {post.date}
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono-jb text-xs text-white/45">
              <Clock className="h-3.5 w-3.5" /> {post.readTime}
            </span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.02] text-white"
          >
            {post.title}
          </motion.h1>
          <p className="mt-5 text-lg text-white/60 leading-relaxed">{post.excerpt}</p>
          <p className="mt-6 font-mono-jb text-xs text-white/40">By {post.author}</p>
        </Container>
      </section>

      {/* Cover */}
      <Container className="max-w-4xl">
        <Reveal className="relative overflow-hidden rounded-3xl border border-white/10">
          <img src={post.cover} alt={post.title} className="h-64 md:h-[420px] w-full object-cover" />
          <div className="absolute inset-0 ring-1 ring-inset ring-gold/10 rounded-3xl" />
        </Reveal>
      </Container>

      {/* Body */}
      <Section className="pt-14 md:pt-16">
        <Container className="max-w-3xl">
          <article data-testid="blog-post-body">
            {post.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </article>

          <div className="mt-14 border-t border-white/10 pt-8">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 font-semibold text-gold hover:text-white transition-colors"
            >
              Want this run for your pipeline? Talk to the lab
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Container>
      </Section>

      {/* Related */}
      <Section className="pt-0" data-testid="blog-related">
        <Container>
          <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-white">
            Keep reading
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-navy-800 transition-[border-color,transform] duration-300 hover:-translate-y-1.5 hover:border-gold/50"
                >
                  <div className="relative h-36 overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-800 to-transparent" />
                  </div>
                  <div className="p-6">
                    <span className="font-mono-jb text-[10px] uppercase tracking-widest text-gold/70">
                      {p.category}
                    </span>
                    <h3 className="mt-2 font-display text-base font-semibold leading-snug text-white group-hover:text-gold transition-colors">
                      {p.title}
                    </h3>
                    <span className="mt-4 inline-flex items-center gap-2 font-mono-jb text-xs text-white/50">
                      Read <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <CtaBand />
    </div>
  );
}
